import { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';

import { SnowLogListTable } from './Components/SnowLogListTable';
import { PropertySelector } from './Components/PropertySelector';
import { GenerateSnowIceRemovalLogModal } from './Components/GenerateSnowIceRemovalLogModal';
import { DeleteSnowLogConfirmationModal } from './Components/DeleteSnowLogConfirmationModal';

import {
  defaultSnowLog,
  getSnowLogInitialValues,
  SnowIceRemovalLogValidationSchema,
} from './Components/FormikHelpers';

import { getPropertyList } from '../../../Inventory/pages/FacilityInventory/Helpers/Api';

import {
  createSnowLog,
  getSnowLogList,
  updateSnowLog,
  deleteSnowLog,
  getDistinctActions,
  getDistinctCompletedBy,           // ← NEW
  exportSnowLogsToExcel,
} from './Components/FormikHelpers/Api';
import { setLoading } from '../../../../store/actions';

const GenerateSnowIceRemovalLog = () => {
  const dispatch = useDispatch();

  const [properties, setProperties] = useState([]);
  const [snowLogs, setSnowLogs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedSnowLog, setSelectedSnowLog] = useState(null);
  const [distinctActions, setDistinctActions] = useState([]);
  const [distinctCompletedBy, setDistinctCompletedBy] = useState([]); // ← NEW

  const [filters, setFilters] = useState({
    property_id: '',
    building_name: '',
    startDate: '',
    endDate: '',
    actions: '',
    completedBy: '',      
    completedRole: '',    
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;

    setUser(currentUser);

    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      Swal.fire('Error', 'Failed to load properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getUserFromToken]);

  const fetchSnowLogs = useCallback(async () => {
    try {
      dispatch(setLoading(true));

      const params = {
        propertyId: filters.property_id || undefined,
        building_name: filters.building_name || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        actions: filters.actions || undefined,
        completedBy: filters.completedBy || undefined,   
        completedRole: filters.completedRole || undefined,   
      };

      const response = await getSnowLogList(params);
      setSnowLogs(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch snow logs:', error);
      setSnowLogs([]);
      Swal.fire('Error', 'Failed to load snow/ice removal logs', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, filters]);

  const submitSnowLog = async (values) => {
    try {
      dispatch(setLoading(true));

      const payload = {
        ...values,
        attachments: values.attachments || [],
      };

      let logId;

      if (selectedSnowLog?._id) {
        await updateSnowLog(selectedSnowLog._id, payload);
        logId = selectedSnowLog._id;
      } else {
        const response = await createSnowLog(payload);
        logId = response?.data?._id || response?._id;
      }

      return logId;
    } catch (error) {
      console.error('Failed to save snow log:', error);
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleExportExcel = () => {
    if (!filters.property_id) {
      toast.warn("Please select a property first");
      return;
    }

    exportSnowLogsToExcel({
      propertyId: filters.property_id,
      building_name: filters.building_name || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    });
  };

  const formik = useFormik({
    initialValues: defaultSnowLog,
    validationSchema: SnowIceRemovalLogValidationSchema,
    onSubmit: async () => { },
    enableReinitialize: true,
  });

  const handleAddClick = () => {
    setSelectedSnowLog(null);
    formik.resetForm({
      values: {
        ...defaultSnowLog,
        property_details: {
          property_id: filters.property_id,
          building_name: filters.building_name,
        },
      },
    });
    setIsModalOpen(true);
  };

  const handleEditClick = async (log) => {
    setSelectedSnowLog(log);
    setIsLoadingDetails(true);
    try {
      const initialValues = await getSnowLogInitialValues(log._id);
      formik.setValues({
        ...initialValues,
        _id: log._id,
      });
      setIsModalOpen(true);
    } catch (err) {
      Swal.fire('Error', 'Failed to load log details', 'error');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSnowLog?._id) return;

    try {
      dispatch(setLoading(true));
      await deleteSnowLog(selectedSnowLog._id);
      Swal.fire('Deleted', 'Snow/Ice Removal Log deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setSelectedSnowLog(null);
      fetchSnowLogs();
    } catch (err) {
      Swal.fire('Error', 'Failed to delete log', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchSnowLogs();
  }, [fetchSnowLogs]);

  useEffect(() => {
    const loadActions = async () => {
      try {
        const actions = await getDistinctActions();
        setDistinctActions(actions);
      } catch (err) {
        console.error('Failed to load distinct actions:', err);
        toast.error('Could not load action filter options');
      }
    };
    loadActions();
  }, []);

  useEffect(() => {
    const loadCompletedBy = async () => {
      try {
        const users = await getDistinctCompletedBy();
        setDistinctCompletedBy(users);
      } catch (err) {
        console.error('Failed to load completed-by users:', err);
      }
    };
    loadCompletedBy();
  }, []);

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Snow & Ice Removal Logs</title>
      </MetaTags>

      <div className="container-fluid">
        <Breadcrumbs title="Maintenance" breadcrumbItem="Snow & Ice Removal Logs" />

        <PropertySelector
          propertyList={properties}
          selectedFilters={filters}
          onPropertySelectionChange={handleFilterChange}
          onAddClick={handleAddClick}
          onExportExcel={handleExportExcel}
          isLoading={isLoadingDetails}
          user={user}
          distinctActions={distinctActions}
          distinctCompletedBy={distinctCompletedBy} 
        />

        <SnowLogListTable
          items={snowLogs}
          onDelete={(log) => {
            setSelectedSnowLog(log);
            setIsDeleteModalOpen(true);
          }}
          onEdit={handleEditClick}
          onRefresh={fetchSnowLogs}
          userRole={user?.role || 'other'}
        />

        <GenerateSnowIceRemovalLogModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          formik={formik}
          filters={filters}
          isEdit={!!selectedSnowLog}
          onRefresh={fetchSnowLogs}
          submitSnowLog={submitSnowLog}
          userRole={user?.role || 'other'}
        />

        <DeleteSnowLogConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={handleDeleteConfirm}
          snowLog={selectedSnowLog}
        />
      </div>
    </div>
  );
};

export { GenerateSnowIceRemovalLog };